import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminDashboard from "@/pages/AdminDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import FacturiFotoPage from "@/pages/FacturiFotoPage";
import LandingPage from "@/pages/LandingPage";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Buffer cu ultimele erori de API (captate automat pentru raportarea problemelor)
export const recentApiErrors = [];
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      recentApiErrors.unshift({
        endpoint: error.config?.url,
        method: error.config?.method,
        status: error.response?.status || null,
        detail: error.response?.data?.detail || error.message || null,
        time: new Date().toISOString(),
      });
      if (recentApiErrors.length > 10) recentApiErrors.pop();
    } catch { /* noop */ }
    return Promise.reject(error);
  }
);

// Auth context
export const useAuth = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Synchronously initialize auth state from localStorage
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(userData);
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.clear();
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(token);
    setUser(userData);
  };

  const logout = () => {
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Reset state
    setToken(null);
    setUser(null);
  };

  const switchRole = async (newRole) => {
    try {
      const response = await axios.post(`${API}/auth/switch-role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.token) {
        const updatedUser = {
          ...user,
          role: response.data.role,
          permissions: response.data.permissions
        };
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setToken(response.data.token);
        setUser(updatedUser);
        return { success: true, message: response.data.message };
      }
    } catch (error) {
      console.error('Failed to switch role:', error);
      return { success: false, message: error.response?.data?.detail || 'Eroare la schimbarea rolului' };
    }
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.includes(permission) || false;
  };

  return { user, token, login, logout, switchRole, hasPermission, isLoading };
};

// Protected route component
const ProtectedRoute = ({ children, requiredRole, user, isLoading }) => {
  // Wait for auth state to be initialized
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Se încarcă...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Roluri care au acces la dashboard-ul admin (toate în afară de driver)
  const adminRoles = ['admin', 'fleet_manager', 'dispatcher', 'contabilitate', 'juridic', 'tesa'];
  const isAdminRole = adminRoles.includes(user.role) || (user.role && user.role !== 'driver');
  
  // Check if user has the required role (either active or in roles array)
  if (requiredRole) {
    const userRoles = user.roles || [user.role];
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    const hasRole = allowedRoles.some(r => user.role === r || userRoles.includes(r));
    
    if (!hasRole) {
      return <Navigate to={isAdminRole ? '/admin' : '/driver'} replace />;
    }
  }
  return children;
};

function App() {
  const auth = useAuth();

  // Show loading while auth state is initializing
  if (auth.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Se încarcă...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage auth={auth} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute user={auth.user} isLoading={auth.isLoading}>
                {auth.user?.role !== 'driver' ? (
                  <AdminDashboard auth={auth} />
                ) : (
                  <Navigate to="/driver" />
                )}
              </ProtectedRoute>
            }
          />
          <Route
            path="/driver"
            element={
              <ProtectedRoute user={auth.user} isLoading={auth.isLoading}>
                <DriverDashboard auth={auth} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facturi-foto"
            element={
              <ProtectedRoute user={auth.user} isLoading={auth.isLoading}>
                <FacturiFotoPage auth={auth} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={<LandingPage />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;